import { t } from "@lingui/core/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { Provider } from "jotai";
import { Suspense, useCallback, useEffect } from "react";
import { ScrollView } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import {
  useIsClubSellingMemberships,
  useIsClubSellingStandardPackages,
} from "@/features/Club";
import { FacilityLogo, useIsUserSignedIn } from "@/features/Session";
import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { Container } from "@whoosh/design-system/src/apps/PatronApp/layout/Container";
import { ScreenHeader } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenHeader";
import { Button } from "@whoosh/design-system/src/components/controls";
import { ArrowLeftIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { Box } from "@whoosh/design-system/src/components/primitive";

import { StoreLinkedPackageModal } from "../../StoreLinkedPackageModal";
import { StoreProductList } from "../../StoreProductList";
import {
  PackageType,
  StoreProductPageQuery,
} from "./__generated__/StoreProductPageQuery.graphql";

type Props = {
  type: PackageType;
  selectedPackageId?: string;
  onSelectedPackageClose?: () => void;
};

export const StoreProductPage = ({
  selectedPackageId,
  onSelectedPackageClose,
  type,
}: Props) => {
  const [queryRef, loadQuery] = useQueryLoader<StoreProductPageQuery>(
    StoreProductPageQueryString
  );

  const doQuery = useCallback(() => {
    loadQuery({ packageType: type }, { fetchPolicy: "store-and-network" });
  }, [loadQuery, type]);

  useEffect(() => {
    doQuery();
  }, [doQuery]);

  const { top, bottom, left, right } = useSafeAreaInsets();

  return (
    <Box
      flex={1}
      bg="surface-primary"
      style={{
        paddingTop: top,
        paddingBottom: bottom,
        paddingLeft: left,
        paddingRight: right,
      }}
    >
      <Suspense fallback={null}>
        {queryRef ? (
          <StoreProductPageInner
            queryRef={queryRef}
            selectedPackageId={selectedPackageId}
            onSelectedPackageClose={onSelectedPackageClose}
            type={type}
            onPackageAuthComplete={doQuery}
          />
        ) : null}
      </Suspense>
    </Box>
  );
};

type InnerProps = {
  queryRef: PreloadedQuery<StoreProductPageQuery>;
  type: PackageType;
  selectedPackageId?: string;
  onSelectedPackageClose?: () => void;
  onPackageAuthComplete: () => void;
};

const StoreProductPageInner = ({
  queryRef,
  selectedPackageId,
  onSelectedPackageClose,
  type,
  onPackageAuthComplete,
}: InnerProps) => {
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();

  const data = usePreloadedQuery(StoreProductPageQueryString, queryRef);

  const club = data.session?.currentClientProfile?.member?.club;
  const clubName = club?.name;
  const packages = club?.packages;

  const title = type === "MEMBERSHIP" ? t`Memberships` : t`Packages`;

  const isClubSellingMemberships = useIsClubSellingMemberships(club);
  const isClubSellingPackages = useIsClubSellingStandardPackages(club);

  const canReturnToStore =
    type === "MEMBERSHIP" ? isClubSellingPackages : isClubSellingMemberships;

  const canGoBack = navigation.canGoBack() && canReturnToStore;

  const userIsSignedIn = useIsUserSignedIn(data.session);

  const onBackPress = useCallback(() => {
    if (canGoBack) {
      navigation.goBack();
    } else if (userIsSignedIn) {
      navigation.navigate("Home", { screen: "MainHome" });
    }
  }, [canGoBack, navigation, userIsSignedIn]);

  return (
    <Provider>
      <Container>
        <ScreenHeader
          title={title}
          subHeading={clubName}
          includeHorizontalPadding={false}
          leftItem={
            canGoBack || userIsSignedIn ? (
              <Button
                accessibilityLabel={t`Back button`}
                variant="secondary"
                leftIcon={<ArrowLeftIcon />}
                onPress={onBackPress}
              />
            ) : (
              <FacilityLogo
                clubRef={club}
                imageProps={{
                  style: { height: 56, width: 56, borderRadius: 24 },
                }}
              />
            )
          }
        />
      </Container>
      <ScrollView>
        <Container>
          <StoreProductList
            sessionRef={data.session}
            packageConnectionRef={packages}
            onPackageAuthComplete={onPackageAuthComplete}
          />
          {selectedPackageId && onSelectedPackageClose ? (
            <StoreLinkedPackageModal
              packageId={selectedPackageId}
              type={type === "MEMBERSHIP" ? "MEMBERSHIP" : "STANDARD"}
              onClose={onSelectedPackageClose}
            />
          ) : null}
        </Container>
      </ScrollView>
    </Provider>
  );
};

const StoreProductPageQueryString = graphql`
  query StoreProductPageQuery($packageType: PackageType) {
    session {
      ...StoreProductList_session
      ...useIsUserSignedIn_session
      currentClientProfile {
        member {
          club {
            name
            ...FacilityLogoFragment_club
            ...useIsClubSellingMemberships_club
            ...useIsClubSellingStandardPackages_club
            packages(
              packageType: $packageType
              assignable: true
              saleAvailability: PUBLIC
            ) {
              ...StoreProductList_packageConnection
            }
          }
        }
      }
    }
  }
`;
